import Vue from 'vue';
import { Commit, Dispatch, ActionContext } from 'vuex';
import config from '@config';
import StoreUtil from '@app/store/StoreUtil';
import User from '@core/models/user/User';
import LoginCredentials from '@models/authentication/LoginCredentials';
import AuthenticationError from '@errors/authentication/AuthenticationError';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { BetSlipPurchaseType } from '@models/shared/betSlip/betSlipEnums';
import * as Account from './account';
import Ticket from '@core/models/tickets/Ticket';
import { TicketStatus } from '@core/models/tickets/enums';
import { Subscription } from 'rxjs';
import { IState } from '@app/store';
import router, { routeNames } from '@app/router';
import { isBehindLogin } from '@app/router/utils';
import Modals from '@store/modules/ui/shared/modal/modals';
import ScheduledUtil from '@core/utils/scheduled/ScheduledUtil';
import { differenceInHours } from 'date-fns';
import TicketService from '@core/services/tickets/TicketService';
import UserService from '@core/services/user/UserService';
import ClientStorage from '@core/utils/storage/ClientStorage';
import { NotificationType } from '@core/models/user/enums';
import { IAuthenticationState, Getters, AuthenticationGetters } from './types';
import RegisterInput from '@countryModels/authentication/RegisterInput';
import { UtmData } from '@app/analytics/utm';
import { deleteIdCardVerificationPending, isIdCardVerificationJustBeenApproved } from '@models/user/UserDataService';

export const SET_USER = 'SET_USER';
export const SET_USER_DATA = 'SET_USER_DATA';
export const LAST_SESSION_DURATION_SHOWN_DATE = 'LAST_SESSION_DURATION_SHOWN_DATE';

export const SESSION_START = 'users:sessionStart';

let sessionInitialized = false;

function defaultState(): IAuthenticationState {
    return {
        user: null,
        lastSessionDurationShownDate: ClientStorage.get('user:lastSessionDurationShownDate'),
    };
}

let ticketsSubscription: Subscription | null;
let scheduledJobIds: number[] = [];

type UserPropertyKey = keyof User;
export interface UserFieldMutationPayload {
    key: UserPropertyKey;
    value: User[UserPropertyKey];
}

const getters: Getters = {
    user: StoreUtil.createSimpleGetter('user'),
    lastSessionDurationShownDate: StoreUtil.createSimpleGetter('lastSessionDurationShownDate'),
    ...Account.getters,
};

export default {
    getters,

    namespaced: true,

    state: defaultState(),

    mutations: {
        [SET_USER]: StoreUtil.createSimpleMutator('user'),
        [SET_USER_DATA](state: IAuthenticationState, payload: UserFieldMutationPayload) {
            Vue.set(state.user!, payload.key, payload.value);
        },
        [LAST_SESSION_DURATION_SHOWN_DATE]: StoreUtil.createSimpleMutator('lastSessionDurationShownDate'),
    },

    actions: {
        register: StoreUtil.asyncErrorGuard((
            { dispatch }: { dispatch: Dispatch },
            registerInput: RegisterInput & UtmData,
        ) => {
            return authenticationService.register(registerInput);
        }),

        login: StoreUtil.asyncErrorGuard(async (
            { dispatch }: { dispatch: Dispatch },
            credentials: LoginCredentials) => {
            const user = await authenticationService.login(credentials);
            user.balance = await UserService.getInstance().getPlayerBalance();
            dispatch('postLogin', user);
            dispatch('ui/sportOffer/betSlip/setPurchaseType', BetSlipPurchaseType.online, { root: true });
        }),

        postLogin(context: ActionContext<IAuthenticationState, IState>, user: User) {
            ClientStorage.set(SESSION_START, Date.now());
            setSessionState(user, context);
            context.dispatch('ui/user/messages/showPendingMessages', null, { root: true });
        },

        isAuthenticated: StoreUtil.asyncErrorGuard(async (context: ActionContext<IAuthenticationState, IState>) => {
            // In addition to checking whether the user is currently in session,
            // this action clears the session state when that is not the case.
            const authenticated = await authenticationService.isAuthenticated();
            if (!authenticated) {
                clearSessionState(context);
            }
            return authenticated;
        }),

        initializeSession: StoreUtil.asyncErrorGuard(async (context: ActionContext<IAuthenticationState, IState>) => {
            try {
                const [user, details] = await Promise.all([
                    authenticationService.getSession(),
                    UserService.getInstance().getUserDetails()
                ]);
                user.balance = await UserService.getInstance().getPlayerBalance();
                Object.assign(user, details);
                /* sessionId with appended userId is used by the tickets-api (currently the only endpoint which uses
                the session id). Login returns the sessionId in the same format. sessionId|user */
                user.sessionId = `${user.sessionId}|${user.userId}`;
                user.welcomeBonus = context.state.user && context.state.user.welcomeBonus || false;
                setSessionState(user, context);
            } catch (e) {
                if (e instanceof AuthenticationError) {
                    clearSessionState(context);
                    return;
                }

                throw e;
            }
        }),

        logout: StoreUtil.asyncErrorGuard(async (
            { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
            doServerLogout: boolean = true
        ) => {
            try {
                if (doServerLogout) {
                    await authenticationService.logout();
                }
                dispatch('postLogout');
            } catch (e) {
                if (e instanceof AuthenticationError) {
                    dispatch('postLogout');
                    return;
                }
                throw e;
            }
        }),

        postLogout({ commit, dispatch, rootGetters }: { commit: Commit, dispatch: Dispatch, rootGetters: any }) {
            clearSessionState({ dispatch, commit });
            const currentRoute = rootGetters['navigation/route'];
            if (isBehindLogin(currentRoute)) {
                router.push({
                    name: routeNames.landing,
                });
            }
        },

        /**
         * Session duration is currently set to 10800 seconds (3 hours). Session is extended on every secure
         * api call (player session is provided on the call) if the difference between initial session's
         * expiry date (login time + 3hours) and current time + 3 hours is bigger than 18 minutes
         * so if player logs in at 9:30, estimated expiry would be 12:30.
         * If secure action is done at 9:40, difference between 12:30 and 12:40 is 10 minutes
         * which is smaller than 18 minutes, session is not prolonged
         * If first secure action is done at 9:50, difference between 12:30 and 12:50 is 20minutes,
         * which is bigger than 18 minutes, session is prolonged to 12:50 (current secure action time + 3 hours)
         *
         * So basically, after login, at least 18minutes need to pass in order for the session
         * to be prolonged for another 3 hours
         */
        sessionExpired({ dispatch }: { dispatch: Dispatch }) {
            dispatch('ui/modal/closeModal', null, { root: true });
            dispatch('logout', false);
            dispatch(
                'ui/modal/setModal',
                {
                    code: Modals.error.code,
                    data: {
                        button: {
                            label: 'ok'
                        },
                        text: {
                            content: 'Your session has expired',
                            className: 'instruction',
                        }
                    }
                },
                { root: true }
            );
        },

        activateAccount: StoreUtil.asyncErrorGuard(
            async (context: ActionContext<IAuthenticationState, IState>, token: string) => {
                const user = await authenticationService.activateAccount(token);
                user.balance = await UserService.getInstance().getPlayerBalance();
                const details = await UserService.getInstance().getUserDetails();
                Object.assign(user, details);
                user.welcomeBonus = true;
                ClientStorage.set(SESSION_START, Date.now());
                setSessionState(user, context);
            }),

        setUserData({ commit }: { commit: Commit }, { key, value }: { key: string, value: any }) {
            commit(SET_USER_DATA, { key, value });
        },

        // this action in located here because the callback is a concern of the user
        // if the subscription were set in the tickets ui store there would still be a link to this store
        // through a commit on the user
        _subscribeToTicketChanges(context: ActionContext<IAuthenticationState, IState>) {
            const user = context.state.user!;
            TicketService.getInstance().startStreaming();
            ticketsSubscription = TicketService.getInstance()
                .subscribeToTicketsChanges(user.sessionId, async (ticket: Ticket) => {
                    const recentTickets = context.rootGetters['data/tickets/ticketsMap'];
                    const existingTicket = recentTickets[ticket.id];
                    if (existingTicket
                        && existingTicket.status !== TicketStatus.win
                        && ticket.status === TicketStatus.win) {
                        // Should we debounce this in case multiple tickets finish at the same time?
                        const balance = await UserService.getInstance().getPlayerBalance();
                        context.commit(SET_USER_DATA, { key: 'balance', value: balance });
                    }
                });
        },
    },
};

function startPeriodicSessionJobs(getters: any, dispatch: Dispatch, commit: Commit) {
    async function showSessionDurationModal() {
        const sessionStart = ClientStorage.get(SESSION_START);
        if (!sessionStart) {
            return;
        }

        const isAuthenticated = await dispatch('isAuthenticated');
        if (!isAuthenticated) {
            dispatch('sessionExpired');
        }

        const currentTime = Date.now();
        const hoursOnline = differenceInHours(currentTime, sessionStart);

        if (hoursOnline && (!getters.lastSessionDurationShownDate
            || differenceInHours(currentTime, getters.lastSessionDurationShownDate))) {
            dispatch(
                'ui/modal/pushModal',
                {
                    code: Modals.sessionDuration.code,
                    data: {
                        hoursOnline
                    },
                    isUnique: true
                },
                { root: true });

            ClientStorage.set('user:lastSessionDurationShownDate', currentTime);
            commit(LAST_SESSION_DURATION_SHOWN_DATE, currentTime);
        }
    }

    function refreshBalance() {
        dispatch('ui/user/account/getBalance', null, { root: true });
    }

    const jobs = [
        {
            job: showSessionDurationModal,
            period: config.app.user.sessionDurationNotificationPeriod,
        },
        {
            job: refreshBalance,
            period: config.app.user.balanceRefreshPeriod,
        }
    ];
    jobs.forEach(({ job, period }) => scheduledJobIds.push(ScheduledUtil.scheduleTask(job, period)));
}

function endPeriodicSessionJobs() {
    scheduledJobIds.forEach(ScheduledUtil.removeScheduledTask);
    scheduledJobIds = [];
}

function clearSessionState({ dispatch, commit }: { dispatch: Dispatch, commit: Commit }) {
    commit(SET_USER, null);
    ClientStorage.remove(SESSION_START);
    sessionInitialized = false;
    dispatch('ui/sportOffer/betSlip/setPurchaseType', BetSlipPurchaseType.offline, { root: true });
    dispatch('ui/lotto/betSlip/setPurchaseType', BetSlipPurchaseType.offline, { root: true });
    dispatch('ui/lotto/clearUserSpecificData', null, { root: true });
    dispatch('data/tickets/clearAll', null, { root: true });
    dispatch('ui/ticketsPage/clearAll', null, { root: true });
    dispatch('ui/ticketsStack/clearState', null, { root: true });
    dispatch('ui/sportOffer/clearDockedStream', null, { root: true });
    dispatch('ui/sportOffer/closeFloatingStream', null, { root: true });

    if (ticketsSubscription) {
        TicketService.getInstance().stopStreaming();
    }
    ticketsSubscription = null;

    endPeriodicSessionJobs();
}

function setSessionState(user: User, { state, commit, dispatch, getters }:
    { state: IAuthenticationState, commit: Commit, dispatch: Dispatch, getters: any }) {
    // ensures that this action is called at most once per page load

    if (sessionInitialized) {
        throw new Error('Attempted to set session state twice.');
    }
    sessionInitialized = true;

    commit(SET_USER, user);
    dispatch('_subscribeToTicketChanges');
    dispatch('ui/lotto/betSlip/setPurchaseType', BetSlipPurchaseType.online, { root: true });
    dispatch('ui/user/preferences/initializeUserPreferences', {}, { root: true });

    handleIdCardVerificationStatus(dispatch, user);
    startPeriodicSessionJobs(getters, dispatch, commit);
}

function handleIdCardVerificationStatus(dispatch: Dispatch, user: User) {
    if (!isIdCardVerificationJustBeenApproved(user)) {
        return;
    }

    deleteIdCardVerificationPending(user);
    dispatch(
        'ui/user/notifications/removeNotification',
        NotificationType.idCardVerificationPending,
        { root: true }
    );

    dispatch(
        'ui/user/notifications/addNotification',
        {
            title: 'identity document confirm',
            type: NotificationType.idCardVerificationApproved
        },
        { root: true }
    );
}

export {
    IAuthenticationState,
    AuthenticationGetters,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/data/authentication/authenticationIndex.ts